/**
 * BundleBuilderFormPreviewWrapper
 * --------------------------------
 * A dedicated layout wrapper for the BundleBuilder Editor V2 (RHF + A/B
 * testing). Forked from the shared `FormPreviewWrapper2` (which still
 * serves Placeholder unchanged) rather than from Volume's simpler
 * wrapper, because BundleBuilder carries substantially more bundle-type
 * -specific UI than Volume ever did: product-as-a-page creation/restore
 * (ProductIntent / BundleAsProductError), a mobile/desktop preview
 * toggle + BBTemplate modal, and the "bundle_builder_link" store
 * preview links. All of that is preserved here as-is; the only things
 * that changed are the data source (react-hook-form instead of
 * @shopify/react-form) and the new A/B testing affordances
 * (variantSwitcher slot, Enable/Stop A/B Testing actions).
 *
 * It consumes form state from the nearest FormProvider (react-hook-form)
 * via useFormContext() – the caller (BundleBuilder.tsx) must ensure a
 * FormProvider wraps this.
 */

import {
    ActionList,
    Badge,
    Banner,
    Button,
    ButtonGroup,
    FormLayout,
    Icon,
    Popover,
    SkeletonBodyText,
    SkeletonDisplayText,
    SkeletonThumbnail,
    Tabs,
    TextField,
    Tooltip,
} from "@shopify/polaris";
import styles from "./BundleBuilderFormPreviewWrapper.module.css";
import React, { Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getBundleRoute } from "../../../constants";
import {
    ArrowLeftIcon,
    DuplicateIcon,
    StatusActiveIcon,
    MenuHorizontalIcon,
    ViewIcon,
    VariantIcon,
    MobileIcon,
    DesktopIcon,
} from "@shopify/polaris-icons";
import { getBoolean } from "../../../../utilities/functions";
import { endPoints, getData, postData } from "../../../../utilities/networkCalls";
import { SaveBar } from "@shopify/app-bridge-react";
import { BundleBuilderConfigInterface } from "../../Types";
import RenderError from "../../../../components/RenderError/RenderError";
import { BundleTypes, isBundleExpired, isBundleScheduled } from "../../constants";
import SuggestFeature from "../../../../components/SuggestFeature/SuggestFeature";
import { AppsContext } from "../../BundlesHomePage";
import LimitReachedPopup from "../../../../components/Plans/LimitReachedPopup";
import ActivateSnippetModal from "../../../../components/ActivateSnippetModal/ActivateSnippetModal";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import CalloutNudgeBanner from "../../../../components/CalloutNudgeBanner";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTooltipAdmin from "../../../../components/CustomTooltipAdmin/CustomTooltipAdmin";
import { useFormContext } from "react-hook-form";
import { VariantFieldContext } from "../../../../components/FormFields";
import { BASE_TARGET } from "../hooks/useBundleEditor";
import { useStableDeepValue } from "../../../../hooks/useStableDeepValue";
import useBundleMixpanelHook from "../../../../hooks/useBundleMixpanelHook";
import { usePlatform } from "../../../../hooks/usePlatform";
import BundleAsProductError from "../../../../components/BundleAsProductError/BundleAsProductError";
import RHFProductIntent from "../../../../components/Intents/RHFProductIntent";
import ShowOnStoreModal from "../../../../components/ShowOnStoreModal/ShowOnStoreModal";
import BundleBuilderModalPreview from "../../../../components/FormPreviewWrapper2/BundleBuilderModalPreview";
import BBTemplate from "./BBTemplate/BBTemplate";
import { MdOutlineKeyboardCommandKey } from "react-icons/md";
import InfoIconOverride from "../../../../components/Icons/InfoIconOverride";

// ---------------------------------------------------------------------------
// Loading skeleton helpers
// ---------------------------------------------------------------------------

export const FormLoadingSkeleton = () => {
    return (
        <div style={{ paddingLeft: "13px" }}>
            {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} style={{ marginTop: "10px" }}>
                    <div style={{ width: "55%", marginBottom: "7px" }}>
                        <SkeletonBodyText lines={1} />
                    </div>
                    <SkeletonDisplayText maxWidth="50%" />
                </div>
            ))}
        </div>
    );
};

export const PreviewLoadingSkeleton = () => {
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "white",
                padding: "10px",
                borderRadius: "12px",
            }}
        >
            <div style={{ margin: "0 auto 10px auto" }}>
                <div
                    style={{
                        width: "150px",
                        textAlign: "center",
                    }}
                >
                    <SkeletonDisplayText maxWidth={`${100}%`} />
                </div>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ width: "30px", marginRight: "10px" }}>
                    <SkeletonThumbnail size={"small"} />
                </div>
                <div
                    style={{
                        width: "700px",
                        marginBottom: "7px",
                        marginRight: "80px",
                    }}
                >
                    <SkeletonDisplayText maxWidth={`${100}%`} />
                </div>
                <SkeletonBodyText lines={2} />
            </div>

            <div
                style={{
                    textAlign: "center",
                    margin: "12px 0",
                    fontSize: "16px",
                    color: "#e3e3e3",
                }}
            >
                +
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ width: "30px", marginRight: "10px" }}>
                    <SkeletonThumbnail size={"small"} />
                </div>
                <div
                    style={{
                        width: "700px",
                        marginBottom: "20px",
                        marginRight: "80px",
                    }}
                >
                    <SkeletonDisplayText maxWidth={`${100}%`} />
                </div>
                <SkeletonBodyText lines={2} />
            </div>

            <div style={{ display: "flex", marginTop: "10px" }}>
                <div
                    style={{
                        width: "100%",
                    }}
                >
                    <SkeletonDisplayText maxWidth={`${100}%`} />
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BundleBuilderFormPreviewWrapperProps {
    bundleConfig: BundleBuilderConfigInterface;
    loading: boolean;
    savingBundle?: boolean;
    setSavingBundle?: Dispatch<SetStateAction<boolean>>;
    activatingBundle?: boolean;
    onSave: (param?: { src: string }) => Promise<void>;
    onDiscard: (param?: { src: string }) => void;
    onActivate: (currentStatus: boolean) => Promise<void>;
    onDuplicate: (name: string) => Promise<void>;
    forAiBundle?: boolean;
    selected: number;
    setSelected: (index: number) => void;
    renderForms: React.ReactNode[];
    renderPreview: React.ReactNode;
    /** Optional A/B VariantTabs component rendered above the form tabs */
    variantSwitcher?: React.ReactNode;
    handleNext: (index: number, name: string) => void;
    inspectorMode?: boolean;
    setInspectorMode?: (mode: boolean) => void;
    /** From useBundleEditor — stays true across variant switches until save/discard */
    hasUnsavedChanges?: boolean;
    /** Enables A/B testing (only used while it's not yet enabled) */
    onEnableAB?: () => void;
    /** Stops A/B testing (only used while it's enabled) */
    onStopAB?: () => void;
    /** True when the store's current plan doesn't include A/B testing */
    abTestingLocked?: boolean;
    lockedTooltipContent: string | ReactNode;
    formPreviewWrapperRef?: any;
    onlyPreview?: boolean;
    setPreviewType?: (type: number) => void;
}

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

const TABS = [
    { id: "Content-1", content: "Content", panelID: "Content-1" },
    { id: "Design-1", content: "Design", panelID: "Design-1" },
    { id: "Advanced-1", content: "Advanced", panelID: "Advanced-1" },
    // { id: "Settings-1", content: "Settings", panelID: "Settings-1" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BundleBuilderFormPreviewWrapper = ({
    bundleConfig,
    loading = false,
    savingBundle = false,
    setSavingBundle = () => {},
    activatingBundle = false,
    onSave,
    onDiscard,
    onActivate,
    onDuplicate,
    forAiBundle = false,
    selected,
    renderForms,
    renderPreview,
    variantSwitcher,
    handleNext,
    inspectorMode = false,
    setInspectorMode = () => {},
    hasUnsavedChanges,
    onEnableAB,
    onStopAB,
    abTestingLocked = false,
    lockedTooltipContent,
    formPreviewWrapperRef,
    onlyPreview = false,
    setPreviewType = () => {},
}: BundleBuilderFormPreviewWrapperProps) => {
    // ------- context -------
    const goTo = useNavigate();
    const { setPublishCount } = useContext(AppsContext);
    const { plansInfo, appName, globalSettingsInfo } = useContext(AppConfigContext);
    const { isExhausted, isTrial, currentPlan, currentOrders } = plansInfo;
    const platform = usePlatform();
    const { trackFormMenu, trackInspectorModeToggle } = useBundleMixpanelHook();

    // ------- RHF (from parent FormProvider) -------
    const {
        formState: { isDirty, errors },
    } = useFormContext<BundleBuilderConfigInterface>();

    // // Bridge for legacy consumers (ProductIntent, BBTemplate) that still expect
    // // the old `dispatchBundleConfig({ key, payload, isComplex } | { init })`
    // // reducer-action shape — writes straight into the RHF store instead.
    // const dispatchBundleConfig = useCallback(
    //     (action: any) => {
    //         if (action?.key && action.isComplex) {
    //             const current = (getValues(action.key) as object) || {};
    //             setValue(action.key, { ...current, ...action.payload }, { shouldDirty: true });
    //         } else if (action?.key) {
    //             setValue(action.key, action.payload, { shouldDirty: true });
    //         } else if (action?.init) {
    //             reset({ ...getValues(), ...action.init });
    //         }
    //     },
    //     [setValue, getValues, reset],
    // );

    const stableModalBundleConfig = useStableDeepValue(bundleConfig);

    // ------- A/B: which variant the admin currently has selected -------
    const bundleEditorContext = useContext(VariantFieldContext);
    const previewVariantId =
        bundleEditorContext?.abEnabled && bundleEditorContext.activeTarget !== BASE_TARGET
            ? bundleEditorContext.activeTarget
            : null;

    // ------- local state -------
    const [showLimitPopup, setShowLimitPopup] = useState(false);
    const [showEnableCoreSnippetModal, setShowEnableCoreSnippetModal] = useState(false);
    const [showEnableCoreSnippetModalForDraftPreview, setShowEnableCoreSnippetModalForDraftPreview] = useState(false);
    const [duplicateConfirmModal, setDuplicateConfirmModal] = useState(false);
    const [duplicateBundleLoading, setDuplicateBundleLoading] = useState(false);
    const [newBundleTitle, setNewBundleTitle] = useState("");
    const [duplicateErr] = useState("");
    const [showStoreModal, setShowStoreModal] = useState(false);
    const [previewLink, setPreviewLink] = useState("");
    const [popoverActive, setPopoverActive] = useState(false);
    const [openBBTemplateModal, setOpenBBTemplateModal] = useState(false);
    const [previewBtnLoading, setPreviewBtnLoading] = useState(false);
    // 1 -> mobile, 2 -> desktop
    const [selectedPreviewView, setSelectedPreviewView] = useState(1);
    const { bId } = useParams();

    // ------- Shopify SaveBar sync -------
    // Show save bar if the current form is dirty OR if any variant was previously
    // dirtied (hasUnsavedChanges stays true across variant switches, and also
    // covers changes — like adding/removing a variant — that never touch RHF's
    // own dirty state).
    const hasChanges = isDirty || (hasUnsavedChanges ?? false);
    useEffect(() => {
        if (hasChanges) {
            shopify.saveBar.show("bundle-save-bar");
        } else {
            shopify.saveBar.hide("bundle-save-bar");
        }
    }, [hasChanges]);

    // ------- Publish count sync -------
    useEffect(() => {
        if (!loading) {
            setPublishCount((bundleConfig as any).publish_count || 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    // ------- Plan redirect -------
    useEffect(() => {
        if (!currentPlan || Object.keys(currentPlan).length === 0) {
            goTo("/billing");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPlan]);

    // ------- Ref exposed for AI-bundle flows (AiQuality/AiBundlesWrapper) -------
    useEffect(() => {
        if (formPreviewWrapperRef)
            formPreviewWrapperRef.current = {
                handleBundleSave: onSave,
                loader: savingBundle,
                disabled: savingBundle || activatingBundle || loading,
            };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bundleConfig, savingBundle, activatingBundle, loading]);

    // ------- Tab change -------
    const handleTabChange = useCallback(
        (selectedTabIndex: number) => {
            handleNext(selectedTabIndex, TABS[selectedTabIndex]?.content);
        },
        [handleNext],
    );

    // ------- Inspector toggle -------
    const handleInspectorToggle = () => {
        if (loading) return;
        const newMode = !inspectorMode;
        setInspectorMode(newMode);
        trackInspectorModeToggle({
            bundleType: BundleTypes.BUNDLE_BUILDER,
            ctaType: "Toggle Inspector",
            cta: "Inspector Mode Toggle",
            inspectorMode: newMode,
        });
    };

    // ------- Preview URL helpers (bundle_builder_link is the source of truth) -------
    const withPreviewVariantParam = (url: string) => {
        if (!url || !previewVariantId) return url;
        try {
            const withParam = new URL(url);
            withParam.searchParams.set("kefi_preview_variant", previewVariantId);
            return withParam.toString();
        } catch (error) {
            return url;
        }
    };

    const handleStoreModalDisplay = (link = "") => {
        const countKey = `bundle_settings_${BundleTypes.BUNDLE_BUILDER}`;
        const settingsObj = globalSettingsInfo?.globalSettings?.[countKey];
        if (settingsObj?.show_on_store_modal_count === 0) {
            if (link) setPreviewLink(link);
            setShowStoreModal(true);
            postData({
                url: endPoints.updateBundleSettings,
                payload: {
                    bundle_type: BundleTypes.BUNDLE_BUILDER,
                    bundles_settings: {
                        show_on_store_modal_count: 1,
                    },
                },
            });
            settingsObj.show_on_store_modal_count = 1;
        }
    };

    const handlePreview = async () => {
        trackFormMenu({ cta: "Form Menu", ctaType: "Show on Store", bundleType: BundleTypes.BUNDLE_BUILDER });
        const url = withPreviewVariantParam(`${bundleConfig?.bundle_builder_link}`);
        handleStoreModalDisplay(url);
        window.open(url, "_blank");
    };

    const handleDraftPreview = async () => {
        setPreviewBtnLoading(true);
        const isExtensionEnabled = await getData({ url: endPoints.extensionStatus });
        if (isExtensionEnabled.data.status === 0) {
            setPreviewBtnLoading(false);
            setShowEnableCoreSnippetModalForDraftPreview(true);
            return;
        }
        const fId = bundleConfig?.form_id;
        window.open(withPreviewVariantParam(`${bundleConfig?.bundle_builder_link}?form_id=${fId}`), "_blank");
        setPreviewBtnLoading(false);
    };

    const handleActivateClick = async (currentStatus: boolean) => {
        if (!isTrial && isExhausted && !currentStatus) {
            setShowLimitPopup(true);
            return;
        }
        if (!currentStatus) {
            const isExtensionEnabled = await getData({ url: endPoints.extensionStatus });
            if (isExtensionEnabled.data.status === 0) {
                setShowEnableCoreSnippetModal(true);
                return;
            }
        }
        onActivate(currentStatus);
    };

    // ------- Duplicate -------
    const handleDuplicate = async () => {
        if (!newBundleTitle) return;
        setDuplicateBundleLoading(true);
        try {
            await onDuplicate(newBundleTitle);
            setDuplicateConfirmModal(false);
            setNewBundleTitle("");
        } finally {
            setDuplicateBundleLoading(false);
        }
    };

    const shakeSaveBar = () => {
        const saveBar = document.getElementById("bundle-save-bar");
        if (saveBar) {
            shopify.saveBar.leaveConfirmation();
        }
    };

    // ------- Tooltip for publish -------
    const tooltipContent: React.ReactNode = hasChanges ? (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <img src="/svgs/upArrow.png" alt="Warning" style={{ width: 25, height: 25, transform: "rotate(-20deg)" }} />
            Please save the offer first!
        </span>
    ) : (bundleConfig?.form_id?.length ?? 0) > 0 && !bundleConfig?.product_bundle_id ? (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>Please restore the product first!</span>
    ) : null;

    // ------- Derived error list for RenderError -------
    // RHF/zod nested errors (e.g. `bundles_design.bundle_title_size`) don't carry a
    // `.message` on the parent key — only on the leaf. Recurse into the error tree so
    // we surface the actual validation message (already human-readable — the schema's
    // superRefine helpers bake the field's display name into it) instead of a generic
    // "bundles_design is invalid".
    const flattenErrors = (errObj: any, path: string[] = []): { name: string; message: string }[] => {
        if (!errObj || typeof errObj !== "object") return [];
        if (typeof errObj.message === "string" && errObj.message.length) {
            if (appName === "bundles-local") {
                const name = path.join(".");
                return [{ name, message: name ? `${name}: ${errObj.message}` : errObj.message }];
            }
            return [{ name: path.join("."), message: errObj.message }];
        }
        const results: { name: string; message: string }[] = [];
        Object.keys(errObj).forEach((key) => {
            if (key === "type" || key === "ref" || key === "types") return;
            const val = errObj[key];
            if (val && typeof val === "object") {
                results.push(...flattenErrors(val, [...path, key]));
            }
        });
        return results;
    };
    const globalErrorsArray = flattenErrors(errors);

    const isRestoreNeeded = !loading && (bundleConfig?.form_id?.length ?? 0) > 0 && !bundleConfig?.product_bundle_id;

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <div className={styles.formOuterWrapper} id="formPreviewWrapper">
            <div className={styles.formPreviewWrapper} key="bundle-builder">
                {/* ---- Sticky meta-bar ---- */}
                <div
                    style={{
                        backgroundColor: forAiBundle ? "transparent" : "#f1f1f1",
                        position: "sticky",
                        top: "0",
                        zIndex: "110",
                        paddingBottom: forAiBundle ? 0 : "16px",
                    }}
                >
                    {!forAiBundle && (
                        <div className={styles.metaBarWrapper}>
                            {/* Left side: back + title + badge */}
                            <div className={styles.metaBarWrapperLeft}>
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!hasChanges) {
                                            trackFormMenu({
                                                cta: "Form Menu",
                                                ctaType: "Back",
                                                bundleType: BundleTypes.BUNDLE_BUILDER,
                                            });
                                            goTo(
                                                bundleConfig?.form_id?.length
                                                    ? getBundleRoute(BundleTypes.OFFERS)
                                                    : getBundleRoute("/"),
                                            );
                                        } else {
                                            shopify.saveBar.leaveConfirmation();
                                        }
                                    }}
                                    className={styles.backBtn}
                                >
                                    <ArrowLeftIcon width={18} />
                                </div>
                                <span className={styles.bundleName}>
                                    {bId ? "Edit Bundle Builder" : "Create Bundle Builder"}
                                </span>
                                {!!bundleConfig?.form_id?.length && (
                                    <>
                                        {getBoolean(bundleConfig?.status) ? (
                                            isBundleScheduled(bundleConfig.start_date) ? (
                                                <Badge tone="attention">Scheduled</Badge>
                                            ) : isBundleExpired(bundleConfig.end_date) ? (
                                                <Badge>Expired</Badge>
                                            ) : bundleConfig?.is_paused ? (
                                                <Tooltip content="This offer is currently paused because you've reached your plan limit.">
                                                    <Badge tone="warning">Paused</Badge>
                                                </Tooltip>
                                            ) : (
                                                <Badge tone="success">Active</Badge>
                                            )
                                        ) : (
                                            <Badge tone="info">Draft</Badge>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Right side: action buttons */}
                            <div className={styles.metaBarWrapperRight}>
                                {getBoolean(bundleConfig.status) && (
                                    <span className={`${styles.secondaryBtn} ${styles.duplicateButton}`}>
                                        <Button
                                            onClick={handlePreview}
                                            disabled={
                                                !bundleConfig.status ||
                                                !bundleConfig?.form_id?.length ||
                                                loading ||
                                                hasChanges
                                            }
                                        >
                                            Show on Store
                                        </Button>
                                    </span>
                                )}
                                {!getBoolean(bundleConfig.status) && (bundleConfig?.form_id?.length ?? 0) > 0 && (
                                    <span className={`${styles.secondaryBtn} ${styles.duplicateButton}`}>
                                        <Button
                                            onClick={handleDraftPreview}
                                            disabled={!bundleConfig?.form_id || loading || hasChanges}
                                            loading={previewBtnLoading}
                                        >
                                            Preview on Store
                                        </Button>
                                    </span>
                                )}
                                <span className={`${styles.secondaryBtn} ${styles.duplicateButton}`}>
                                    <Button
                                        onClick={() => {
                                            trackFormMenu({
                                                cta: "Form Menu",
                                                ctaType: "Duplicate Modal Triggered",
                                                bundleType: BundleTypes.BUNDLE_BUILDER,
                                            });
                                            setDuplicateConfirmModal(true);
                                        }}
                                        disabled={!bundleConfig?.form_id?.length || loading || hasChanges}
                                    >
                                        Duplicate
                                    </Button>
                                </span>

                                <CustomTooltipAdmin content={lockedTooltipContent}>
                                    <span className={`${styles.secondaryBtn} ${styles.duplicateButton}`}>
                                        {bundleEditorContext?.abEnabled ? (
                                            <Button
                                                onClick={onStopAB}
                                                disabled={
                                                    abTestingLocked ||
                                                    loading ||
                                                    (hasChanges && !!bundleConfig?.form_id?.length)
                                                }
                                            >
                                                Stop A/B Testing
                                            </Button>
                                        ) : (
                                            <Button
                                                onClick={onEnableAB}
                                                disabled={abTestingLocked || loading || savingBundle}
                                            >
                                                Enable A/B Testing
                                            </Button>
                                        )}
                                    </span>
                                </CustomTooltipAdmin>

                                {tooltipContent ? (
                                    <Tooltip content={tooltipContent} preferredPosition="above">
                                        <span
                                            style={{ display: "inline-block" }}
                                            onClick={() => {
                                                if (hasChanges) shakeSaveBar();
                                            }}
                                        >
                                            <Button
                                                variant="primary"
                                                onClick={() => handleActivateClick(!!getBoolean(bundleConfig.status))}
                                                loading={activatingBundle}
                                                disabled={
                                                    !bundleConfig?.form_id?.length ||
                                                    activatingBundle ||
                                                    loading ||
                                                    hasChanges ||
                                                    isRestoreNeeded
                                                }
                                            >
                                                {getBoolean(bundleConfig.status) ? "Unpublish" : "Publish"}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Button
                                        variant="primary"
                                        onClick={() => handleActivateClick(!!getBoolean(bundleConfig.status))}
                                        loading={activatingBundle}
                                        disabled={
                                            !bundleConfig?.form_id?.length ||
                                            activatingBundle ||
                                            loading ||
                                            hasChanges ||
                                            isRestoreNeeded
                                        }
                                    >
                                        {getBoolean(bundleConfig.status) ? "Unpublish" : "Publish"}
                                    </Button>
                                )}

                                <div className={styles.moreActionsIcon}>
                                    <Popover
                                        active={popoverActive}
                                        activator={
                                            <div onClick={() => setPopoverActive((v) => !v)}>
                                                <Icon source={MenuHorizontalIcon} tone="base" />
                                            </div>
                                        }
                                        autofocusTarget="first-node"
                                        onClose={() => setPopoverActive(false)}
                                    >
                                        <ActionList
                                            actionRole="menuitem"
                                            items={[
                                                {
                                                    prefix: <Icon source={DuplicateIcon} tone="base" />,
                                                    content: "Duplicate",
                                                    onAction: () => setDuplicateConfirmModal(true),
                                                    disabled: !bundleConfig?.form_id?.length || loading || hasChanges,
                                                },
                                                {
                                                    prefix: <Icon source={VariantIcon} tone="base" />,
                                                    content: bundleEditorContext?.abEnabled
                                                        ? "Stop A/B Testing"
                                                        : "Enable A/B Testing",
                                                    onAction: bundleEditorContext?.abEnabled ? onStopAB : onEnableAB,
                                                    disabled:
                                                        abTestingLocked ||
                                                        loading ||
                                                        (bundleEditorContext?.abEnabled
                                                            ? hasChanges && !!bundleConfig?.form_id?.length
                                                            : savingBundle),
                                                },
                                                {
                                                    prefix: <Icon source={StatusActiveIcon} tone="base" />,
                                                    content: getBoolean(bundleConfig.status)
                                                        ? "Deactivate"
                                                        : "Activate",
                                                    disabled:
                                                        !bundleConfig?.form_id?.length ||
                                                        activatingBundle ||
                                                        loading ||
                                                        hasChanges ||
                                                        isRestoreNeeded,
                                                    onAction: () =>
                                                        handleActivateClick(!!getBoolean(bundleConfig.status)),
                                                },
                                                {
                                                    prefix: <Icon source={ViewIcon} tone="base" />,
                                                    content: "Show on Store",
                                                    disabled:
                                                        !bundleConfig.status ||
                                                        !bundleConfig?.form_id?.length ||
                                                        loading ||
                                                        hasChanges,
                                                    onAction: handlePreview,
                                                },
                                            ]}
                                        />
                                    </Popover>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ---- Validation errors ---- */}
                <RenderError submitErrors={globalErrorsArray} />
                <BundleAsProductError bundleConfig={bundleConfig} />

                {/* ---- "Bundle product was deleted" restore banner ---- */}
                {isRestoreNeeded && (
                    <div style={{ marginBottom: 14 }}>
                        <Banner title="This bundle may not work properly" tone="warning">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span>The bundle product was deleted from your Shopify store.</span>
                                <Button
                                    variant="secondary"
                                    onClick={() => onSave({ src: "Restore Button" })}
                                    loading={savingBundle}
                                >
                                    Restore
                                </Button>
                            </div>
                        </Banner>
                    </div>
                )}

                {/* ---- Main panels ---- */}
                <div
                    className={styles.panels}
                    style={
                        loading && !onlyPreview
                            ? {}
                            : onlyPreview
                              ? {
                                    margin: "auto",
                                    display: "flex",
                                    justifyContent: "center",
                                    minWidth: "240px",
                                    maxWidth: "400px",
                                }
                              : forAiBundle
                                ? { padding: "1rem", background: "#f1f1f1" }
                                : {}
                    }
                >
                    {/* Left: A/B switcher + content tabs */}
                    {!onlyPreview && (
                        <div className={styles.leftSection}>
                            {variantSwitcher}
                            <Tabs tabs={TABS} selected={selected} onSelect={handleTabChange} disabled={loading}>
                                <div
                                    className={styles.selectedTabPanel}
                                    style={{ pointerEvents: loading ? "none" : "all" }}
                                >
                                    {loading ? <FormLoadingSkeleton /> : renderForms[selected]}
                                </div>
                            </Tabs>
                        </div>
                    )}

                    {/* Right: live preview */}
                    <div
                        className={styles.rightSection}
                        id="rightSectionContainerId"
                        style={{ top: forAiBundle ? 69 : 64 }}
                    >
                        {!loading && (bundleConfig?.form_id?.length ?? 0) > 0 && !!bundleConfig?.product_bundle_id && (
                            <RHFProductIntent shakeSaveBar={shakeSaveBar} setSavingBundle={setSavingBundle} />
                            //  <ProductIntent
                            //     bundleConfig={bundleConfig}
                            //     bundle_as_product={bundleConfig?.bundle_as_product}
                            //     product_bundle_id={bundleConfig?.product_bundle_id || ""}
                            //     dispatchBundleConfig={dispatchBundleConfig}
                            //     setConfig={() => {}}
                            //     shakeSaveBar={shakeSaveBar}
                            //     globalDirty={isDirty}
                            //     setSavingBundle={setSavingBundle}
                            //     product_title={bundleConfig?.title}
                            // />
                        )}

                        <div className={styles.preview} id="bundle_builderPreview">
                            {!onlyPreview && (
                                <div
                                    className={styles.previewWidgetTitle}
                                    style={{ color: loading ? "#b5b5b5" : "#4a4a4a" }}
                                >
                                    <div style={{ zoom: 0.9 }}>
                                        <ButtonGroup variant="segmented">
                                            <Button
                                                pressed={!inspectorMode}
                                                onClick={() => {
                                                    if (inspectorMode) handleInspectorToggle();
                                                }}
                                                disabled={savingBundle || activatingBundle || loading}
                                            >
                                                Preview
                                            </Button>
                                            <Button
                                                pressed={inspectorMode}
                                                onClick={() => {
                                                    if (!inspectorMode) handleInspectorToggle();
                                                }}
                                                disabled={savingBundle || activatingBundle || loading}
                                                children={
                                                    (
                                                        <span className={styles.inspectorModeToggleWrapper}>
                                                            Design Editor
                                                            <Tooltip
                                                                width="wide"
                                                                preferredPosition="above"
                                                                content={
                                                                    <div className={styles.tooltipContent}>
                                                                        <div className={styles.mainTooltipText}>
                                                                            <span className={styles.shortcut}>
                                                                                To edit styles:
                                                                            </span>{" "}
                                                                            Click any element.
                                                                        </div>
                                                                        <div className={styles.tooltipText}>
                                                                            Tip: Hold{" "}
                                                                            <span
                                                                                className={styles.shortcut}
                                                                                style={{
                                                                                    display: "inline-flex",
                                                                                    alignItems: "center",
                                                                                    gap: "2px",
                                                                                }}
                                                                            >
                                                                                {platform === "Mac" ? (
                                                                                    <MdOutlineKeyboardCommandKey
                                                                                        size={13}
                                                                                        style={{
                                                                                            position: "relative",
                                                                                            top: "2px",
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    "Ctrl"
                                                                                )}{" "}
                                                                            </span>{" "}
                                                                            <span className={styles.shortcut}>
                                                                                + Click
                                                                            </span>{" "}
                                                                            to interact with the element normally
                                                                            without leaving Editor Mode.
                                                                        </div>
                                                                    </div>
                                                                }
                                                            >
                                                                <span className={styles.infoIcon}>
                                                                    <InfoIconOverride size={14} />
                                                                </span>
                                                            </Tooltip>
                                                        </span>
                                                    ) as unknown as string
                                                }
                                            ></Button>
                                        </ButtonGroup>
                                    </div>

                                    {(bundleConfig.steps?.[0]?.enable_subSteps
                                        ? bundleConfig.steps[0].subSteps?.[0]?.product_selection === "specific"
                                            ? (bundleConfig.steps[0].subSteps?.[0]?.products?.length ?? 0) >= 1
                                            : (bundleConfig.steps[0].subSteps?.[0]?.collections?.length ?? 0) >= 1
                                        : bundleConfig.steps?.[0]?.product_selection === "specific"
                                          ? (bundleConfig.steps[0].products?.length ?? 0) >= 1
                                          : (bundleConfig.steps?.[0]?.collections?.length ?? 0) >= 1) && (
                                        <Button
                                            onClick={() => setOpenBBTemplateModal(true)}
                                            variant="plain"
                                            disabled={savingBundle || activatingBundle || loading}
                                        >
                                            Change Template
                                        </Button>
                                    )}

                                    {!inspectorMode && (
                                        <span style={{ display: "flex" }}>
                                            <span
                                                onClick={() => {
                                                    setSelectedPreviewView(1);
                                                    setPreviewType(1);
                                                }}
                                                style={{
                                                    cursor: "pointer",
                                                    backgroundColor: selectedPreviewView === 1 ? "#dddddd" : "#ffffff",
                                                    borderRadius: "6px",
                                                    padding: "2px",
                                                }}
                                            >
                                                <Tooltip content="Mobile View">
                                                    <Icon source={MobileIcon} tone={loading ? "subdued" : "base"} />
                                                </Tooltip>
                                            </span>
                                            <span
                                                onClick={() => {
                                                    setSelectedPreviewView(2);
                                                    setPreviewType(2);
                                                }}
                                                style={{
                                                    cursor: "pointer",
                                                    backgroundColor: selectedPreviewView === 2 ? "#dddddd" : "#ffffff",
                                                    borderRadius: "6px",
                                                    padding: "2px",
                                                }}
                                            >
                                                <Tooltip content="Desktop View">
                                                    <Icon source={DesktopIcon} tone={loading ? "subdued" : "base"} />
                                                </Tooltip>
                                            </span>
                                        </span>
                                    )}
                                </div>
                            )}
                            {loading ? <PreviewLoadingSkeleton /> : <>{renderPreview}</>}
                        </div>

                        {!loading && <CalloutNudgeBanner />}
                        {!onlyPreview && !loading && <SuggestFeature bundleType={BundleTypes.BUNDLE_BUILDER} />}
                    </div>
                </div>

                {/* ---- Duplicate modal ---- */}
                {duplicateConfirmModal && (
                    <CustomModal
                        title="Duplicate Bundle"
                        open={duplicateConfirmModal}
                        loading={duplicateBundleLoading}
                        onClose={() => setDuplicateConfirmModal(false)}
                        primaryAction={{
                            content: "Duplicate",
                            disabled: !newBundleTitle || !!duplicateErr.length || duplicateBundleLoading,
                            onAction: handleDuplicate,
                        }}
                        secondaryActions={[
                            {
                                content: "Cancel",
                                onAction: () => setDuplicateConfirmModal(false),
                            },
                        ]}
                    >
                        <FormLayout>
                            <TextField
                                label="Title"
                                placeholder="bundle title"
                                autoComplete=""
                                value={newBundleTitle}
                                onChange={(val) => setNewBundleTitle(val)}
                                error={duplicateErr}
                            />
                        </FormLayout>
                    </CustomModal>
                )}

                {/* ---- Mobile/Desktop full preview modal ---- */}
                <BundleBuilderModalPreview
                    open={selectedPreviewView === 2}
                    onClose={() => setSelectedPreviewView(1)}
                    bundleConfig={stableModalBundleConfig}
                />

                {/* ---- BB template modal ---- */}
                {openBBTemplateModal && (
                    <BBTemplate
                        openBBTemplateModal={openBBTemplateModal}
                        setOpenBBTemplateModal={setOpenBBTemplateModal}
                        bundleConfig={bundleConfig}
                        standAlone={false}
                        forAiBundle={forAiBundle}
                    />
                )}

                {/* ---- Limit popup ---- */}
                {showLimitPopup && (
                    <LimitReachedPopup
                        setPopup={setShowLimitPopup}
                        isExhausted={isExhausted}
                        currentPlan={currentPlan}
                        currentOrders={currentOrders}
                    />
                )}

                {showEnableCoreSnippetModal && (
                    <ActivateSnippetModal
                        setShowEnableCoreSnippetModal={setShowEnableCoreSnippetModal}
                        onSnippetEnabled={() => onActivate(!!getBoolean(bundleConfig.status))}
                    />
                )}
                {showEnableCoreSnippetModalForDraftPreview && (
                    <ActivateSnippetModal
                        setShowEnableCoreSnippetModal={setShowEnableCoreSnippetModalForDraftPreview}
                        onSnippetEnabled={() => handleDraftPreview()}
                    />
                )}

                {showStoreModal && (
                    <ShowOnStoreModal
                        open={showStoreModal}
                        onClose={() => setShowStoreModal(false)}
                        bundleType={BundleTypes.BUNDLE_BUILDER}
                        bundleLink={previewLink}
                    />
                )}

                {/* ---- Shopify Save Bar (appears when form is dirty) ---- */}
                {savingBundle ? (
                    <SaveBar id="bundle-save-bar">
                        <button variant="primary" loading="" onClick={() => onSave({ src: "Top Bar" })} disabled>
                            Save
                        </button>
                        <button onClick={() => onDiscard({ src: "Top Bar" })} disabled>
                            Discard
                        </button>
                    </SaveBar>
                ) : (
                    <SaveBar id="bundle-save-bar">
                        <button variant="primary" onClick={() => onSave({ src: "Top Bar" })}>
                            Save
                        </button>
                        <button onClick={() => onDiscard({ src: "Top Bar" })}>Discard</button>
                    </SaveBar>
                )}
            </div>
        </div>
    );
};

export default BundleBuilderFormPreviewWrapper;
